import { selectedLayersAtom } from '@/atoms';
import { selectedFeatureAtom } from '@/atoms/selection.atom';
import { getPlaceholderAvatar } from '@/core/avatar';
import { propertyBlacklist } from '@/core/config';
import { execute } from '@/graphql/execute';
import { FeatureAttachmentsQuery } from '@/graphql/graphql';
import { useOrganizations } from '@/hooks/useOrganizations';
import { createAttachment } from '@/queries/attachment.query';
import {
  createFeatureAttachmentMutation,
  deleteFeatureAttachmentMutation,
  deleteFeatureMutation,
  featureAttachmentsQuery,
  featureQuery,
} from '@/queries/feature.query';
import { Download, InfoCircleSolid, LabelSolid, Plus, Trash } from '@mynaui/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState, type FunctionComponent } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { formatDateTime } from './BatchList';
import { Coords } from './Coords';
import { FeatureNavigator } from './FeatureNavigator';
import { ID } from './ID';
import PortalTooltip from './PortalTooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from './ui/drawer';
import { Input } from './ui/input';
import { Label } from './ui/label';

interface iFeaturePropertiesProps {}

type FeatureAttachment = FeatureAttachmentsQuery['featureAttachments'][number];

const FeatureProperties: FunctionComponent<iFeaturePropertiesProps> = () => {
  const { hasRole } = useOrganizations();
  const { projectId } = useParams();
  const [selectedFeature, setSelectedFeature] = useAtom(selectedFeatureAtom);
  const layers = useAtomValue(selectedLayersAtom(projectId!));
  const layer = layers.find((layer) => layer.id === selectedFeature?.layerId);
  const queryClient = useQueryClient();

  // Recursive function to get all parent layers upward in the ancestry tree, starting from feature's parent layer
  const getLayerAncestry = (currentLayer: typeof layer | undefined, allLayers: typeof layers): (typeof layer)[] => {
    if (!currentLayer || !currentLayer.parentLayerId) {
      return [];
    }
    const parentLayer = allLayers.find((l) => l.id === currentLayer.parentLayerId);
    if (!parentLayer) {
      return [];
    }
    // Traverse upward, accumulating parents
    return [parentLayer, ...getLayerAncestry(parentLayer, allLayers)];
  };
  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadLabel, setUploadLabel] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Detect if this is a draft feature (sub-task 4.1)
  const isDraft = !selectedFeature?.id || selectedFeature?.isDraft;

  // Query for feature attachments
  const { data: attachmentsData, isLoading: attachmentsLoading } = useQuery({
    queryKey: ['featureAttachments', selectedFeature?.id],
    queryFn: () => execute(featureAttachmentsQuery, { featureId: selectedFeature!.id }),
    enabled: !!selectedFeature?.id && !isDraft,
  });

  const attachments = attachmentsData?.featureAttachments ?? [];

  // Delete attachment mutation
  const deleteAttachmentMutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      return execute(deleteFeatureAttachmentMutation, { id: attachmentId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featureAttachments', selectedFeature?.id] });
    },
  });

  // Create attachment mutation
  const createAttachmentMutation = useMutation({
    mutationFn: async ({ attachmentId, label }: { attachmentId: string; label: string }) => {
      return execute(createFeatureAttachmentMutation, {
        input: {
          featureId: selectedFeature!.id,
          attachmentId,
          label,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featureAttachments', selectedFeature?.id] });
      _resetUploadDialog();
    },
    onError: (error) => {
      toast.error(`Failed to create attachment: ${error.message}`);
    },
  });

  const _resetUploadDialog = () => {
    setUploadDialogOpen(false);
    setUploadLabel('');
    setUploadFile(null);
    setIsUploading(false);
  };

  const _handleUpload = async () => {
    if (!uploadFile || !selectedFeature?.id) return;

    setIsUploading(true);

    const t = toast.loading('Uploading attachment...');
    try {
      // Generate checksum
      const buffer = await uploadFile.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      const byteArray = new Uint8Array(hashBuffer);
      let binary = '';
      for (let i = 0; i < byteArray.length; i++) {
        binary += String.fromCharCode(byteArray[i]);
      }
      const checksum = btoa(binary);

      // Create attachment record and get presigned URL
      const data = await execute(createAttachment, {
        project: projectId,
        filename: uploadFile.name,
        contentType: uploadFile.type || 'application/octet-stream',
        checksumSHA256Base64: checksum,
        type: 'FEATURE_ATTACHMENT',
      });

      const { attachment, uploadUrl } = data?.createAttachment || {};
      if (!attachment?.id || !uploadUrl) {
        throw new Error('Failed to create attachment');
      }

      // Upload file to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: uploadFile,
        headers: {
          'Content-Type': uploadFile.type || 'application/octet-stream',
          'X-Amz-Checksum-Sha256': checksum,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status}`);
      }

      // Link attachment to feature
      await createAttachmentMutation.mutateAsync({
        attachmentId: attachment.id,
        label: uploadLabel || uploadFile.name,
      });
      toast.success('Attachment uploaded', { id: t });
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Failed to upload: ${error instanceof Error ? error.message : 'Unknown error'}`, { id: t });
      setIsUploading(false);
    }
  };

  const _handleDeleteAttachment = async (attachment: FeatureAttachment) => {
    await toast
      .promise(deleteAttachmentMutation.mutateAsync(attachment.id), {
        loading: 'Deleting attachment...',
        success: 'Attachment deleted',
        error: 'Failed to delete attachment',
      })
      .unwrap();
  };

  const _handleDownloadAttachment = async (attachment: FeatureAttachment) => {
    const t = toast.loading('Downloading attachment...');
    try {
      const url = attachment.attachment.url;
      const filename = attachment.attachment.filename ?? 'download';

      const response = await fetch(url, {
        credentials: 'include',
      });

      // Optional: basic error handling
      if (!response.ok) {
        console.error('Failed to fetch file', response.status, response.statusText);
        toast.error('Failed to download attachment', { id: t });
        return;
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      URL.revokeObjectURL(blobUrl);

      toast.success('Attachment downloaded', { id: t });
    } catch (error) {
      toast.error('Failed to download attachment', { id: t });
      console.error('Failed to download attachment', error);
    }
  };

  const _formatFileSize = (bytes: number | null | undefined) => {
    if (!bytes) return '';
    if (bytes < 1000) return `${bytes} B`;
    if (bytes < 1000 * 1000) return `${(bytes / 1000).toFixed(1)} KB`;
    return `${(bytes / (1000 * 1000)).toFixed(1)} MB`;
  };

  useEffect(() => {
    // Skip fetching properties for draft features
    if (!selectedFeature || isDraft) return;
    execute(featureQuery, {
      id: selectedFeature.id,
    }).then((data) => {
      setSelectedFeature((prev) =>
        !prev
          ? prev
          : {
              ...prev,
              properties: data.feature.properties,
            },
      );
    });
  }, [selectedFeature?.id]);

  const _handleClose = () => setSelectedFeature(null);

  const _handleSave = async () => {
    if (!selectedFeature || !selectedFeature.onSave) return;

    await selectedFeature.onSave(selectedFeature);

    // Close drawer for saved features (draft features handle their own closing in onSave)
    if (!isDraft) {
      _handleClose();
    }
  };

  const _handleDelete = async () => {
    if (!selectedFeature) return;
    await execute(deleteFeatureMutation, { id: selectedFeature.id });
    queryClient.invalidateQueries({ queryKey: ['features', selectedFeature.layerId] });
    _handleClose();
  };

  const _handleMove = () => {
    if (!selectedFeature) return;
    setSelectedFeature({
      ...selectedFeature,
      isMoving: true,
    });
  };

  const _handleEdit = () => {
    if (!selectedFeature) return;
    setSelectedFeature({
      ...selectedFeature,
      isEditing: true,
    });
  };

  const layerAncestry = useMemo(() => getLayerAncestry(layer, layers), [layer, layers]);

  // Collect all aliases from the ancestry, starting from root to child,
  // so that the closest (child-most) alias is used when duplicate exists (override upwards).
  const aliases = [...layerAncestry.reverse(), layer]
    .filter(Boolean)
    .flatMap((l) => l!.propertyAliases || [])
    .reduce(
      (acc, alias) => {
        acc[alias.property] = alias.alias;
        return acc;
      },
      {} as Record<string, string>,
    );

  const getPropertyName = (property: string) => {
    return aliases[property] || property;
  };

  return (
    <Drawer open={!!selectedFeature && !selectedFeature.isMoving} onClose={_handleClose}>
      <DrawerContent>
        <DrawerHeader className='w-lg'>
          <DrawerTitle>{isDraft ? 'New Feature' : 'Selected Feature'}</DrawerTitle>
          {!isDraft && <FeatureNavigator className='mt-2' />}
        </DrawerHeader>
        <div className='flex-1 overflow-y-auto'>
          <div className='flex flex-col gap-4 px-4 py-2'>
            <div className='flex flex-col gap-1'>
              <Label>
                <div className='flex gap-1'>
                  SGID
                  <InfoCircleSolid data-tooltip-id='feature-serial-tooltip' className='size-3.5 opacity-60' />
                </div>
              </Label>
              <ID value={selectedFeature?.serial?.toString() || ''} />
            </div>
            <div className='flex flex-col gap-1'>
              <Label>
                <div className='flex gap-1'>
                  Layer
                  <InfoCircleSolid data-tooltip-id='feature-layer-tooltip' className='size-3.5 opacity-60' />
                </div>
              </Label>
              <ID value={layer?.name || ''} />
            </div>
            <PortalTooltip id='feature-serial-tooltip'>
              The internal ID of the feature, generated by SkyGIS. This is not the same as the feature ID.
            </PortalTooltip>
            <PortalTooltip id='feature-layer-tooltip'>
              The parent layer of the feature. This is the layer that the feature belongs to.
            </PortalTooltip>
            {selectedFeature?.geometry.type === 'Point' && (
              <div className='flex flex-col gap-1'>
                <Label>
                  Coordinates {isDraft && <span className='text-xs font-normal opacity-70'>(read-only)</span>}
                </Label>
                <Coords coords={selectedFeature.geometry.coordinates} />

                <PortalTooltip id='coordinates-tooltip'>
                  <span className='text-sm font-medium'>
                    Coordinates <span className='text-xs font-medium opacity-70'>(Click to copy)</span>
                  </span>
                  <div className='font-mono leading-tight mt-0.5'>
                    <div>X: {selectedFeature.geometry.coordinates[0].toFixed(6)}</div>
                    <div>Y: {selectedFeature.geometry.coordinates[1].toFixed(6)}</div>
                    <div>Z: {selectedFeature.geometry.coordinates[2].toFixed(6)}</div>
                  </div>
                </PortalTooltip>
              </div>
            )}
            {Object.entries(selectedFeature?.properties || {})
              .filter(([key]) => !propertyBlacklist.includes(key))
              .map(([key, value]) => {
                const label = getPropertyName(key);
                return (
                  <div key={key} className='flex flex-col gap-2'>
                    <Label>
                      {label}
                      {key !== label && (
                        <LabelSolid data-tooltip-id={`property-label-tooltip-${key}`} className='size-3.5 opacity-60' />
                      )}
                    </Label>
                    <PortalTooltip id={`property-label-tooltip-${key}`}>
                      Alias for <strong className='font-semibold'>{key}</strong>
                    </PortalTooltip>
                    <Input
                      disabled={key === 'ID' || (!isDraft && !selectedFeature?.isEditing)}
                      value={String(value)}
                      onChange={(e) => {
                        if (!selectedFeature) return;
                        setSelectedFeature({
                          ...selectedFeature,
                          properties: {
                            ...selectedFeature.properties,
                            [key]: e.target.value,
                          },
                        });
                      }}
                    />
                  </div>
                );
              })}

            {/* Attachments Section */}
            {!isDraft && (
              <div className='flex flex-col gap-2 mt-4'>
                <Label>Attachments</Label>
                {attachmentsLoading ? (
                  <div className='flex items-center gap-2 text-muted-foreground text-sm'>
                    <Loader2 className='size-4 animate-spin' />
                    Loading attachments...
                  </div>
                ) : attachments.length === 0 ? (
                  <p className='text-muted-foreground text-sm'>No attachments</p>
                ) : (
                  <div className='flex flex-col gap-2'>
                    {attachments.map((attachment) => (
                      <>
                        <div
                          key={attachment.id}
                          className='max-w-120 flex items-center justify-between gap-2 p-2 pl-3 border border border-slate-200/70 bg-slate-50/40 dark:border-stone-900 dark:bg-stone-900/20 rounded-md'>
                          <div className='flex items-center gap-2 flex-1 min-w-0 overflow-hidden'>
                            <div className='flex flex-col min-w-0 leading-tight'>
                              <span className='text-sm font-medium truncate'>{attachment.label}</span>
                              <div className='flex items-center font-medium leading-tight text-xs gap-1 text-slate-700 dark:text-stone-300'>
                                <span
                                  className='flex items-center gap-1 whitespace-nowrap overflow-hidden flex-shrink-0'
                                  data-tooltip-id={`attachment-tooltip-${attachment.id}`}>
                                  <img
                                    src={
                                      attachment.uploadedBy?.avatar?.url ||
                                      getPlaceholderAvatar(
                                        attachment.uploadedBy?.email || attachment.uploadedBy?.id || '',
                                      )
                                    }
                                    alt={attachment.uploadedBy?.name || attachment.uploadedBy?.email || 'Unknown'}
                                    className='size-4 rounded-full object-cover border border-slate-200/70 dark:border-stone-800/70'
                                  />{' '}
                                  {attachment.uploadedBy?.name || attachment.uploadedBy?.email || 'Unknown'}
                                </span>
                                &middot;
                                <span className='text-xs truncate opacity-70'>{attachment.attachment.filename}</span>
                              </div>
                            </div>
                          </div>
                          <div className='flex items-center'>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => _handleDownloadAttachment(attachment)}
                              disabled={deleteAttachmentMutation.isPending}
                              className='aspect-square p-0!'>
                              <Download className='size-4 stroke-2' />
                            </Button>
                            {selectedFeature?.isEditing && hasRole('EDITOR') && (
                              <Button
                                variant='ghost'
                                size='sm'
                                color='red'
                                onClick={() => _handleDeleteAttachment(attachment)}
                                disabled={deleteAttachmentMutation.isPending}
                                className='aspect-square p-0!'>
                                <Trash className='size-4 stroke-2' />
                              </Button>
                            )}
                          </div>
                        </div>
                        <PortalTooltip id={`attachment-tooltip-${attachment.id}`}>
                          <div className='flex items-center gap-1'>
                            <span>Uploaded {formatDateTime(attachment.createdAt)} by</span>
                            <span className='flex items-center gap-1'>
                              <img
                                src={
                                  attachment.uploadedBy?.avatar?.url ||
                                  getPlaceholderAvatar(attachment.uploadedBy?.email || attachment.uploadedBy?.id || '')
                                }
                                alt={attachment.uploadedBy?.name || attachment.uploadedBy?.email || 'Unknown'}
                                className='size-4 rounded-full object-cover border border-slate-200/70 dark:border-stone-800/70'
                              />{' '}
                              {attachment.uploadedBy?.name || attachment.uploadedBy?.email || 'Unknown'}
                            </span>
                          </div>
                        </PortalTooltip>
                      </>
                    ))}
                  </div>
                )}
                {selectedFeature?.isEditing && hasRole('EDITOR') && (
                  <Button variant='outline' onClick={() => setUploadDialogOpen(true)} className='mt-1'>
                    <Plus className='size-4 mr-1' />
                    Upload Attachment
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Attachment</DialogTitle>
              <DialogDescription>Upload a file to attach to this feature.</DialogDescription>
            </DialogHeader>
            <div className='flex flex-col gap-4 py-4'>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='attachment-label'>Label</Label>
                <Input
                  id='attachment-label'
                  placeholder='Enter a label for this attachment'
                  value={uploadLabel}
                  onChange={(e) => setUploadLabel(e.target.value)}
                />
              </div>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='attachment-file'>File</Label>
                <Input id='attachment-file' type='file' onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
                {uploadFile && (
                  <p className='text-sm text-muted-foreground'>
                    Selected: {uploadFile.name} ({_formatFileSize(uploadFile.size)})
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={_resetUploadDialog} disabled={isUploading}>
                Cancel
              </Button>
              <Button onClick={_handleUpload} disabled={!uploadFile || isUploading}>
                {isUploading ? (
                  <>
                    <Loader2 className='size-4 mr-2 animate-spin' />
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <DrawerFooter>
          {/* Sub-task 4.1: Conditionally render buttons based on draft status */}
          {isDraft ? (
            // Draft feature buttons
            <>
              <Button variant='default' onClick={_handleSave}>
                Create Feature
              </Button>
              <Button variant='link' onClick={_handleClose}>
                Cancel
              </Button>
            </>
          ) : (
            // Existing saved feature buttons
            <>
              {hasRole('EDITOR') && selectedFeature?.geometry.type === 'Point' ? (
                <Button variant='outline' onClick={_handleMove} disabled={layer?.isLocked}>
                  Move Feature
                </Button>
              ) : null}
              {hasRole('EDITOR') && (
                <>
                  {selectedFeature?.isEditing ? (
                    <Button variant='default' onClick={_handleSave}>
                      Save
                    </Button>
                  ) : (
                    <Button
                      data-tooltip-id={layer?.isLocked ? 'feature-properties-locked' : undefined}
                      disabled={!selectedFeature || layer?.isLocked}
                      variant='outline'
                      onClick={_handleEdit}>
                      Edit
                    </Button>
                  )}
                </>
              )}
              {hasRole('ADMIN') && (
                <>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        data-tooltip-id={layer?.isLocked ? 'feature-properties-locked' : undefined}
                        disabled={!selectedFeature || layer?.isLocked}
                        variant='destructive'>
                        Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This action cannot be undone. This will permanently delete this feature and remove all data
                          from our servers.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction variant='destructive' onClick={_handleDelete}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
              <PortalTooltip id='feature-properties-locked'>
                Feature cannot be edited because the layer is locked
              </PortalTooltip>
              <Button variant='link' onClick={_handleClose}>
                Cancel
              </Button>
            </>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default FeatureProperties;
